import { useMemo, useCallback } from 'react';
import { useAppData } from './useAppData';

interface BusinessQueryResult {
  answer: string;
  confidence: number;
  dataFound: boolean;
  suggestions: string[];
  vendorBlocks?: { [key: string]: string };
}

export function useBusinessAI() {
  const { lots, expenses, sellers, usuarios, config } = useAppData();

  // Funciones de búsqueda por cliente - ALGORITMO MEJORADO para mayor precisión
  const findClientByName = useCallback((name: string) => {
    if (!lots || !name) return null;

    const nameNormalized = name.toLowerCase().trim();

    // Debug: ver qué lotes tenemos
    console.log('🔍 Buscando cliente:', nameNormalized);
    console.log('📊 Lotes disponibles para búsqueda:', lots.map((lot: any) => ({
      id: lot.id,
      numero: lot.numero,
      buyerName: lot.buyerName,
      comprador: lot.comprador,
      lotes: lot.idLotes
    })));

    // Buscar con diferentes niveles de precisión
    interface Candidate {
      lot: any;
      score: number;
      matchType: string;
      buyerName: string;
    }

    const candidates: Candidate[] = lots.map((lot: any) => {
      const buyerName = (lot.buyerName || lot.comprador || '').toLowerCase();
      const buyerPhone = lot.phoneNumber || lot.telefono || '';

      let score = 0;
      let matchType = '';

      // Puntuación por tipo de coincidencia (mayor puntuación = mejor coincidencia)
      if (buyerName === nameNormalized) {
        score = 100; // Coincidencia exacta
        matchType = 'exacta';
      } else if (buyerName.startsWith(nameNormalized) || nameNormalized.startsWith(buyerName)) {
        score = 90; // Empieza con
        matchType = 'empieza con';
      } else if (buyerName.split(' ').includes(nameNormalized)) {
        score = 80; // Palabra completa
        matchType = 'palabra completa';
      } else if (buyerName.includes(nameNormalized) && nameNormalized.length > 3) {
        score = 70; // Contiene (solo si búsqueda > 3 chars)
        matchType = 'contiene';
      } else if (buyerPhone.includes(nameNormalized)) {
        score = 60; // Por teléfono
        matchType = 'teléfono';
      }

      return { lot, score, matchType, buyerName };
    }).filter((item: Candidate) => item.score > 0);

    // Ordenar por puntuación (mejor coincidencia primero)
    candidates.sort((a: Candidate, b: Candidate) => b.score - a.score);

    if (candidates.length > 0) {
      const bestMatch = candidates[0];
      console.log('✅ Mejor coincidencia encontrada:', {
        nombre: bestMatch.lot.buyerName || bestMatch.lot.comprador,
        lote: bestMatch.lot.id || bestMatch.lot.numero,
        lotes: bestMatch.lot.idLotes,
        score: bestMatch.score,
        matchType: bestMatch.matchType
      });

      // Si hay empate en puntuación, mostrar info de debug
      if (candidates.length > 1 && candidates[0].score === candidates[1].score) {
        console.log('⚠️ Múltiples coincidencias con misma puntuación:',
          candidates.slice(0, 3).map((c: Candidate) => ({
            nombre: c.buyerName,
            score: c.score,
            type: c.matchType
          }))
        );
      }

      return bestMatch.lot;
    }

    console.log('❌ No se encontró cliente para:', nameNormalized);
    return null;
  }, [lots]);

  // Nueva función: Buscar TODOS los clientes que coincidan con el término de búsqueda
  const findAllClientsWithNameMatch = useCallback((searchTerm: string) => {
    if (!lots || !searchTerm) return [];

    const termNormalized = searchTerm.toLowerCase().trim();

    console.log('🔍 Buscando TODOS los clientes que contengan:', termNormalized);

    const allMatches = lots.filter((lot: any) => {
      const buyerName = (lot.buyerName || lot.comprador || '').toLowerCase();

      // Buscar el término en cualquier parte del nombre
      return buyerName.includes(termNormalized);
    });

    console.log(`📋 Encontrados ${allMatches.length} registros que contienen "${termNormalized}":`,
      allMatches.map((lot: any) => ({
        nombre: lot.buyerName || lot.comprador,
        lote: lot.id || lot.numero,
        buyerName: lot.buyerName
      }))
    );

    return allMatches;
  }, [lots]);

  // Nueva función: Generar información completa para MÚLTIPLES clientes
  const generateMultipleCompleteInfo = useCallback((allMatches: any[], searchTerm: string): BusinessQueryResult => {
    if (!allMatches || allMatches.length === 0) {
      return {
        answer: `❌ No se encontraron resultados para "${searchTerm}"`,
        confidence: 0.5,
        dataFound: false,
        suggestions: ['Verificar nombre', 'Buscar por número de lote', 'Ver lotes disponibles']
      };
    }

    let respuesta = `🔍 **Resultados de búsqueda para "${searchTerm}"**\n\n`;
    respuesta += `📊 **${allMatches.length} resultado${allMatches.length > 1 ? 's' : ''} encontrado${allMatches.length > 1 ? 's' : ''}**\n\n`;

    // Separador entre resultados
    const separador = '─'.repeat(40);

    allMatches.forEach((lot, index) => {
      // Generar información completa para cada lote usando la función existente
      const infoCompleta = generateCompleteInfo(lot, searchTerm);

      // Agregar numeración y la información completa
      respuesta += `**${index + 1}.** ${infoCompleta.answer}\n\n`;

      // Agregar separador excepto en el último
      if (index < allMatches.length - 1) {
        respuesta += `${separador}\n\n`;
      }
    });

    return {
      answer: respuesta,
      confidence: 1.0,
      dataFound: true,
      suggestions: [
        `Buscar lote específico`,
        'Ver más detalles',
        'Estado financiero',
        'Próximo pago'
      ]
    };
  }, []);

  // Obtener pagos de un cliente específico - CORREGIDA con deduplicación
  const getClientPayments = useCallback((clientLot: any) => {
    if (!expenses || !clientLot) return [];

    // Obtener números de lote (puede ser individual o grupal)
    const lotNumbers = Array.isArray(clientLot.idLotes) ?
      clientLot.idLotes :
      [clientLot.numero || clientLot.id];

    console.log('🔍 Buscando pagos para lotes:', lotNumbers);

    // PRIORIDAD 1: Usar SOLO los payments del propio lote (fuente única de verdad)
    // Los payments del lote ya incluyen todos los abonos registrados (automáticos y manuales)
    const paymentsFromLot = Array.isArray(clientLot.payments) ? clientLot.payments : [];

    console.log('💰 Pagos encontrados en lote:', paymentsFromLot.length);
    console.log('📋 Detalle de pagos:', paymentsFromLot.map((p: any) => ({
      tipo: p.type,
      monto: p.amount,
      fecha: p.date,
      cuota: p.cuotaNumero
    })));

    // NO combinar con expenses para evitar duplicados
    // Los expenses son solo para referencia de movimientos, pero lot.payments es la fuente autoritativa

    return paymentsFromLot.sort((a: any, b: any) =>
      new Date(a.fecha || a.date).getTime() - new Date(b.fecha || b.date).getTime()
    );
  }, [expenses]);

  // Función para generar cuotas detalladas (debe estar ANTES de getClientFinancialInfo)
  const generateCuotasDetalladas = useCallback((lote: any) => {
    const fechaVenta = lote.saleDate || lote.fechaVenta;
    const numberOfPayments = lote.numberOfPayments || lote.numeroPagos || lote.numPayments;
    const saldoTotal = (lote.totalValue || lote.valorTotal || 0) - (lote.enganche || lote.downPayment || 0);
    const cuotaMensual = numberOfPayments > 0 ? saldoTotal / numberOfPayments : 0;
    const payments = lote.payments || [];

    const cuotas = [];
    if (fechaVenta && numberOfPayments > 0) {
      const fechaVentaDate = new Date(fechaVenta);

      for (let i = 1; i <= numberOfPayments; i++) {
        const fechaVencimiento = new Date(fechaVentaDate);
        fechaVencimiento.setMonth(fechaVencimiento.getMonth() + i);

        // Buscar pagos para esta cuota específica
        const pagosCuota = payments.filter((p: any) =>
          p.type === 'abono' &&
          (p.cuotaNumero === i.toString() || p.cuotaNumero === i)
        );

        const montoPagadoCuota = pagosCuota.reduce((sum: number, p: any) => sum + Number(p.amount), 0);

        let estado = 'Pendiente';
        let montoCuotaMostrar = cuotaMensual;
        const hoy = new Date();

        if (montoPagadoCuota >= cuotaMensual) {
          estado = 'Pagada';
        } else if (montoPagadoCuota > 0) {
          montoCuotaMostrar = cuotaMensual - montoPagadoCuota;
          estado = 'Pendiente Parcial';
        } else if (fechaVencimiento < hoy) {
          estado = 'Vencida';
        }

        cuotas.push({
          numero: i,
          fechaVencimiento: fechaVencimiento.toLocaleDateString('es-GT'),
          fechaVencimientoDate: fechaVencimiento,
          monto: montoCuotaMostrar,
          montoPagado: montoPagadoCuota,
          estado,
          pagosCuota
        });
      }
    }

    return cuotas;
  }, []);

  // Calcular información financiera del cliente - CORREGIDA con deduplicación y cálculo preciso
  const getClientFinancialInfo = useCallback((clientLot: any) => {
    if (!clientLot) return null;

    const payments = getClientPayments(clientLot);

    // Usar los campos correctos de la tabla (valorTotal, enganche, abonosRealizados, etc.)
    const totalValue = parseFloat(clientLot.totalValue || clientLot.valorTotal || '0');

    // CORRECCIÓN: Buscar enganche primero en payments (fuente correcta)
    const pagoEnganche = payments.find((p: any) => (p.tipo === 'enganche' || p.type === 'enganche'));
    const enganche = pagoEnganche ? parseFloat(pagoEnganche.monto || pagoEnganche.amount || '0') : 0;

    // Calcular abonos realizados (sin duplicar)
    const abonos = payments
      .filter((p: any) => (p.tipo === 'abono' || p.type === 'abono'))
      .reduce((sum: number, p: any) => sum + (parseFloat(p.monto || p.amount) || 0), 0);

    const totalPagado = enganche + abonos;
    const saldoPendiente = Math.max(0, totalValue - totalPagado); // No permitir negativos

    // CORRECCIÓN CRÍTICA: Calcular cuotas pagadas correctamente usando generateCuotasDetalladas
    let cuotasPagadas = 0;
    if (clientLot.paymentType === 'pagos' || clientLot.tipoVenta === 'Crédito') {
      const cuotasDetalladas = generateCuotasDetalladas(clientLot);
      cuotasPagadas = cuotasDetalladas.filter(c => c.estado === 'Pagada').length;
    }

    // Debug info mejorado
    console.log('💰 Info financiera calculada (CORREGIDA):', {
      cliente: clientLot.buyerName || clientLot.comprador,
      lote: clientLot.id || clientLot.numero,
      totalValue,
      enganche,
      abonos,
      totalPagado,
      saldoPendiente,
      cuotasPagadas,
      totalPayments: payments.length,
      paymentsAbono: payments.filter((p: any) => p.type === 'abono').length
    });

    return {
      totalValue,
      enganche,
      abonos,
      totalPagado,
      saldoPendiente,
      cuotasPagadas,
      payments,
      status: clientLot.status,
      // Campos adicionales de la tabla
      numeroLote: Array.isArray(clientLot.idLotes) ? clientLot.idLotes.join(', ') : (clientLot.numero || clientLot.id),
      vendedor: clientLot.seller || clientLot.vendedor,
      dpi: clientLot.buyerDPI || clientLot.dpi,
      telefono: clientLot.phoneNumber || clientLot.telefono,
      fechaVenta: clientLot.saleDate || clientLot.fechaVenta,
      tipoVenta: clientLot.paymentType || clientLot.tipoVenta
    };
  }, [getClientPayments, generateCuotasDetalladas]);

  // Función para generar solo información financiera y de pagos
  const generatePaymentInfo = useCallback((lote: any, searchTerm: string): BusinessQueryResult => {
    const financialInfo = getClientFinancialInfo(lote);
    const clientName = lote.buyerName || lote.comprador;
    const loteNumbers = Array.isArray(lote.idLotes) ? lote.idLotes.join(', ') : (lote.numero || lote.id);
    const tipoVenta = lote.paymentType === 'contado' ? 'Contado' : 'Crédito';
    const numeroCuotas = lote.numberOfPayments || lote.numeroPagos || lote.numPayments || 'No definido';

    let respuesta = `💰 **ESTADO FINANCIERO - ${clientName} (Lote ${loteNumbers})**\n\n`;

    // Estado financiero
    respuesta += `💰 **ESTADO FINANCIERO**\n`;
    respuesta += `• **Enganche**: Q.${financialInfo?.enganche.toLocaleString() || '0'}\n`;
    respuesta += `• **Abonos realizados**: Q.${financialInfo?.abonos.toLocaleString() || '0'}\n`;
    respuesta += `• **Total pagado**: Q.${financialInfo?.totalPagado.toLocaleString() || '0'}\n`;
    respuesta += `• **Saldo pendiente**: Q.${financialInfo?.saldoPendiente.toLocaleString() || '0'}\n`;
    respuesta += `• **Cuotas pagadas**: ${financialInfo?.cuotasPagadas || 0} cuotas\n\n`;

    // Variables para estado de pagos
    let cuotasVencidas = 0;
    let proximaFechaPago: Date | null = null;

    // DETALLES DE CRÉDITO si es venta a crédito
    if (tipoVenta === 'Crédito' && numeroCuotas !== 'No definido') {
      respuesta += `📅 **DETALLE DE PAGOS (CRÉDITO)**\n`;

      // Generar cuotas con datos precisos
      const cuotasDetalladas = generateCuotasDetalladas(lote);

      // Mostrar cada cuota con información precisa
      cuotasDetalladas.forEach((cuota: any) => {
        const estadoIcon = cuota.estado === 'Pagada' ? '✅' :
          cuota.estado === 'Vencida' ? '❌' :
            cuota.estado === 'Pendiente Parcial' ? '🟡' : '⏳';

        // Obtener documentos de pago (priorizando numeroDocumento)
        const docs = cuota.pagosCuota?.map((p: any) => p.numeroDocumento || p.noDoc || p.documentNumber || p.receipt || p.receiptNumber || p.boleta || p.boletaDeposito || '').filter(Boolean).join(', ');
        const docInfo = docs ? ` - Doc: ${docs}` : '';

        respuesta += `• **#${cuota.numero}** - ${cuota.fechaVencimiento} - Q.${cuota.monto.toLocaleString()} - ${estadoIcon} ${cuota.estado}${docInfo}\n`;
      });

      // Calcular estados usando datos precisos
      const cuotasPagadas = cuotasDetalladas.filter(c => c.estado === 'Pagada').length;
      const cuotasPendientes = cuotasDetalladas.filter(c => c.estado !== 'Pagada').length;
      const cuotasVencidasList = cuotasDetalladas.filter(c => c.estado === 'Vencida');
      cuotasVencidas = cuotasVencidasList.length;

      // Encontrar próxima cuota pendiente
      const proximaCuota = cuotasDetalladas.find(c => c.estado === 'Pendiente');
      if (proximaCuota) {
        proximaFechaPago = proximaCuota.fechaVencimientoDate;
      }

      // Encontrar primera cuota vencida para calcular días de atraso
      const primeraVencida = cuotasVencidasList.length > 0 ? cuotasVencidasList[0] : null;
      const diasVencimiento = primeraVencida ?
        Math.floor((new Date().getTime() - primeraVencida.fechaVencimientoDate.getTime()) / (1000 * 60 * 60 * 24)) : 0;

      respuesta += `\n📊 **Resumen de pagos**:\n`;
      respuesta += `• **Cuotas pagadas**: ${cuotasPagadas}/${numeroCuotas}\n`;
      respuesta += `• **Cuotas pendientes**: ${cuotasPendientes}\n`;

      if (cuotasVencidas > 0) {
        // Cliente con atraso
        const montoVencido = cuotasVencidasList.reduce((sum, c) => sum + c.monto, 0);
        respuesta += `• **🚨 CUOTAS VENCIDAS**: ${cuotasVencidas} cuotas\n`;
        respuesta += `• **⏰ Días de atraso**: ${diasVencimiento} días\n`;
        respuesta += `• **📅 Primera vencida**: ${primeraVencida?.fechaVencimiento}\n`;
        respuesta += `• **💰 Monto en atraso**: Q.${montoVencido.toLocaleString()}\n`;
        respuesta += `• **Estado**: 🔴 **CON ATRASO - REQUIERE ATENCIÓN**\n`;
      } else if (proximaFechaPago) {
        // Cliente al día
        const hoy = new Date();
        const diasHastaVencimiento = Math.floor((proximaFechaPago.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));

        respuesta += `• **📅 Próxima cuota**: ${proximaFechaPago.toLocaleDateString('es-GT')}\n`;

        if (diasHastaVencimiento <= 7) {
          respuesta += `• **⚠️ Próximo vencimiento**: En ${diasHastaVencimiento} días\n`;
          respuesta += `• **Estado**: 🟠 **PRÓXIMO A VENCER**\n`;
        } else {
          respuesta += `• **⏳ Días restantes**: ${diasHastaVencimiento} días\n`;
          respuesta += `• **Estado**: 🟢 **AL DÍA**\n`;
        }
      } else if (financialInfo?.saldoPendiente !== undefined && financialInfo.saldoPendiente <= 0) {
        respuesta += `• **Estado**: ✅ **PAGADO COMPLETO**\n`;
      } else {
        respuesta += `• **Estado**: ⚪ **SIN CRONOGRAMA DEFINIDO**\n`;
      }

      // Mostrar cronograma de cuotas pendientes con fechas y montos precisos
      const cuotasPendientesList = cuotasDetalladas.filter(c => c.estado !== 'Pagada');
      if (cuotasPendientesList.length > 0 && cuotasPendientesList.length <= 5) {
        respuesta += `\n📋 **Cronograma de cuotas pendientes**:\n`;
        cuotasPendientesList.forEach((cuota: any) => {
          const iconoEstado = cuota.estado === 'Vencida' ? '🚨' : '📅';
          respuesta += `• ${iconoEstado} **Cuota #${cuota.numero}** - ${cuota.fechaVencimiento} - Q.${cuota.monto.toLocaleString()}\n`;
        });
      }
    } else {
      respuesta += `ℹ️ **Venta al contado** - No hay cronograma de cuotas\n`;
    }

    return {
      answer: respuesta,
      confidence: 1.0,
      dataFound: true,
      suggestions: [
        'Información completa cliente',
        'Ver otros pagos',
        'Buscar otro lote',
        'Cuotas vencidas'
      ]
    };
  }, [getClientFinancialInfo, generateCuotasDetalladas]);

  // Función para generar información completa (toda la tabla + detalles de crédito)
  const generateCompleteInfo = useCallback((lote: any, searchTerm: string): BusinessQueryResult => {
    const financialInfo = getClientFinancialInfo(lote);
    const clientName = lote.buyerName || lote.comprador;

    // Obtener el nombre real del vendedor
    const vendedorId = lote.seller || lote.vendedor || lote.sellerName;
    let vendedorName = 'No asignado';
    if (vendedorId && sellers && sellers.length > 0) {
      const vendedorData = sellers.find((s: any) =>
        s.id === vendedorId || s.name === vendedorId
      );
      vendedorName = vendedorData?.name || vendedorId;
    }

    // Información básica de la tabla
    const loteNumbers = Array.isArray(lote.idLotes) ? lote.idLotes.join(', ') : (lote.numero || lote.id);
    const dpi = lote.buyerDPI || lote.dpi || 'No registrado';
    const telefono = lote.phoneNumber || lote.telefono || 'No registrado';
    const fechaVenta = lote.saleDate || lote.fechaVenta;
    const tipoVenta = lote.paymentType === 'contado' ? 'Contado' : 'Crédito';
    const numeroCuotas = lote.numberOfPayments || lote.numeroPagos || lote.numPayments || 'No definido';
    const boletaPago = lote.depositSlip || lote.boletaDeposito || 'No registrado';
    const reciboCaja = lote.receiptNumber || lote.reciboCaja || 'No registrado';

    // Construir respuesta con TODA la información de la tabla
    let respuesta = `📋 **INFORMACIÓN COMPLETA - ${searchTerm.toUpperCase()}**\n\n`;

    // Información del comprador
    respuesta += `👤 **COMPRADOR**\n`;
    respuesta += `• **Nombre**: ${clientName || 'No registrado'}\n`;
    respuesta += `• **DPI**: ${dpi}\n`;
    respuesta += `• **Teléfono**: ${telefono}\n\n`;

    // Información del lote
    respuesta += `🏠 **LOTE(S)**\n`;
    respuesta += `• **Número**: ${loteNumbers}\n`;
    respuesta += `• **Valor total**: Q.${financialInfo?.totalValue.toLocaleString() || 'No definido'}\n\n`;

    // Información de la venta
    respuesta += `💼 **VENTA**\n`;
    respuesta += `• **Vendedor**: ${vendedorName || 'No asignado'}\n`;
    respuesta += `• **Fecha de venta**: ${fechaVenta ? new Date(fechaVenta).toLocaleDateString('es-GT') : 'No registrada'}\n`;
    respuesta += `• **Tipo de venta**: ${tipoVenta}\n`;
    respuesta += `• **Número de cuotas**: ${numeroCuotas}\n`;
    respuesta += `• **Boleta de pago**: ${boletaPago}\n`;
    respuesta += `• **Recibo de caja**: ${reciboCaja}\n\n`;

    // Información financiera detallada
    respuesta += `💰 **ESTADO FINANCIERO**\n`;
    respuesta += `• **Enganche**: Q.${financialInfo?.enganche.toLocaleString() || '0'}\n`;
    respuesta += `• **Abonos realizados**: Q.${financialInfo?.abonos.toLocaleString() || '0'}\n`;
    respuesta += `• **Total pagado**: Q.${financialInfo?.totalPagado.toLocaleString() || '0'}\n`;
    respuesta += `• **Saldo pendiente**: Q.${financialInfo?.saldoPendiente.toLocaleString() || '0'}\n`;
    respuesta += `• **Cuotas pagadas**: ${financialInfo?.cuotasPagadas || 0} cuotas\n\n`;

    // Variables para estado de pagos
    let cuotasVencidas = 0;
    let proximaFechaPago: Date | null = null;

    // DETALLES DE CRÉDITO si es venta a crédito
    if (tipoVenta === 'Crédito' && numeroCuotas !== 'No definido') {
      respuesta += `📅 **DETALLE DE PAGOS (CRÉDITO)**\n`;

      // Generar cuotas con datos precisos
      const cuotasDetalladas = generateCuotasDetalladas(lote);

      // Mostrar cada cuota con información precisa
      cuotasDetalladas.forEach((cuota: any) => {
        const estadoIcon = cuota.estado === 'Pagada' ? '✅' :
          cuota.estado === 'Vencida' ? '❌' :
            cuota.estado === 'Pendiente Parcial' ? '🟡' : '⏳';

        // Obtener documentos de pago (priorizando numeroDocumento)
        const docs = cuota.pagosCuota?.map((p: any) => p.numeroDocumento || p.noDoc || p.documentNumber || p.receipt || p.receiptNumber || p.boleta || p.boletaDeposito || '').filter(Boolean).join(', ');
        const docInfo = docs ? ` - Doc: ${docs}` : '';

        respuesta += `• **#${cuota.numero}** - ${cuota.fechaVencimiento} - Q.${cuota.monto.toLocaleString()} - ${estadoIcon} ${cuota.estado}${docInfo}\n`;
      });

      // Calcular estados usando datos precisos
      const cuotasPagadas = cuotasDetalladas.filter(c => c.estado === 'Pagada').length;
      const cuotasPendientes = cuotasDetalladas.filter(c => c.estado !== 'Pagada').length;
      const cuotasVencidasList = cuotasDetalladas.filter(c => c.estado === 'Vencida');
      cuotasVencidas = cuotasVencidasList.length;

      // Encontrar próxima cuota pendiente
      const proximaCuota = cuotasDetalladas.find(c => c.estado === 'Pendiente');
      if (proximaCuota) {
        proximaFechaPago = proximaCuota.fechaVencimientoDate;
      }

      // Encontrar primera cuota vencida para calcular días de atraso
      const primeraVencida = cuotasVencidasList.length > 0 ? cuotasVencidasList[0] : null;
      const diasVencimiento = primeraVencida ?
        Math.floor((new Date().getTime() - primeraVencida.fechaVencimientoDate.getTime()) / (1000 * 60 * 60 * 24)) : 0;

      respuesta += `\n📊 **Resumen de pagos**:\n`;
      respuesta += `• **Cuotas pagadas**: ${cuotasPagadas}/${numeroCuotas}\n`;
      respuesta += `• **Cuotas pendientes**: ${cuotasPendientes}\n`;

      if (cuotasVencidas > 0) {
        // Cliente con atraso
        const montoVencido = cuotasVencidasList.reduce((sum, c) => sum + c.monto, 0);
        respuesta += `• **🚨 CUOTAS VENCIDAS**: ${cuotasVencidas} cuotas\n`;
        respuesta += `• **⏰ Días de atraso**: ${diasVencimiento} días\n`;
        respuesta += `• **📅 Primera vencida**: ${primeraVencida?.fechaVencimiento}\n`;
        respuesta += `• **💰 Monto en atraso**: Q.${montoVencido.toLocaleString()}\n`;
        respuesta += `• **Estado**: 🔴 **CON ATRASO - REQUIERE ATENCIÓN**\n`;
      } else if (proximaFechaPago) {
        // Cliente al día
        const hoy = new Date();
        const diasHastaVencimiento = Math.floor((proximaFechaPago.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));

        respuesta += `• **📅 Próxima cuota**: ${proximaFechaPago.toLocaleDateString('es-GT')}\n`;

        if (diasHastaVencimiento <= 7) {
          respuesta += `• **⚠️ Próximo vencimiento**: En ${diasHastaVencimiento} días\n`;
          respuesta += `• **Estado**: 🟠 **PRÓXIMO A VENCER**\n`;
        } else {
          respuesta += `• **⏳ Días restantes**: ${diasHastaVencimiento} días\n`;
          respuesta += `• **Estado**: 🟢 **AL DÍA**\n`;
        }
      } else if (financialInfo?.saldoPendiente !== undefined && financialInfo.saldoPendiente <= 0) {
        respuesta += `• **Estado**: ✅ **PAGADO COMPLETO**\n`;
      } else {
        respuesta += `• **Estado**: ⚪ **SIN CRONOGRAMA DEFINIDO**\n`;
      }

      // Mostrar cronograma de cuotas pendientes con fechas y montos precisos
      const cuotasPendientesList = cuotasDetalladas.filter(c => c.estado !== 'Pagada');
      if (cuotasPendientesList.length > 0 && cuotasPendientesList.length <= 5) {
        respuesta += `\n📋 **Cronograma de cuotas pendientes**:\n`;
        cuotasPendientesList.forEach((cuota: any) => {
          const iconoEstado = cuota.estado === 'Vencida' ? '🚨' : '📅';
          respuesta += `• ${iconoEstado} **Cuota #${cuota.numero}** - ${cuota.fechaVencimiento} - Q.${cuota.monto.toLocaleString()}\n`;
        });
      }
    }

    // Generar sugerencias contextuales
    const sugerencias = [];

    if (cuotasVencidas > 0) {
      sugerencias.push('Ver clientes con atraso');
      sugerencias.push('Contactar cliente urgente');
    } else if (proximaFechaPago) {
      sugerencias.push('Ver próximos vencimientos');
      sugerencias.push('Recordatorio de pago');
    }

    sugerencias.push('Buscar otro cliente');
    sugerencias.push('Ver lotes disponibles');

    return {
      answer: respuesta,
      confidence: 1.0,
      dataFound: true,
      suggestions: sugerencias
    };
  }, [getClientFinancialInfo, generateCuotasDetalladas, sellers]);

  // Buscar lotes disponibles
  const getAvailableLots = useCallback(() => {
    if (!lots) return [];

    return lots.filter((lot: any) =>
      !lot.status || lot.status === 'disponible' || lot.buyerName === '' || !lot.buyerName
    );
  }, [lots]);

  // Estadísticas rápidas - Usando la misma lógica que el dashboard principal
  const businessStats = useMemo(() => {
    if (!lots || !expenses) return null;

    // Función para calcular saldo pendiente (copiada del sistema)
    const calcularSaldoPendiente = (lote: any) => {
      const valor = lote.totalValue || lote.valorTotal || 0;
      const enganche = lote.enganche || lote.downPayment || 0;
      const payments = lote.payments || [];
      const totalPagado = payments
        .filter((p: any) => p.type === 'abono')
        .reduce((sum: number, p: any) => sum + (parseFloat(p.amount) || 0), 0);
      return (valor - enganche) - totalPagado;
    };

    // Contar vendidos (contado) considerando lotes agrupados
    const vendidos = lots
      .filter((lot: any) => lot.status === 'vendido')
      .reduce((sum: number, lot: any) => {
        if (Array.isArray(lot.idLotes) && lot.idLotes.length > 1) {
          return sum + lot.idLotes.length;
        }
        return sum + 1;
      }, 0);

    // Separar lotes a crédito entre pendientes y pagados
    const lotesCredito = lots.filter((lot: any) => lot.status === 'crédito');
    const creditoPendiente = lotesCredito
      .filter((lot: any) => calcularSaldoPendiente(lot) > 0.01) // Con saldo pendiente
      .reduce((sum: number, lot: any) => {
        if (Array.isArray(lot.idLotes) && lot.idLotes.length > 1) {
          return sum + lot.idLotes.length;
        }
        return sum + 1;
      }, 0);

    const creditoPagado = lotesCredito
      .filter((lot: any) => calcularSaldoPendiente(lot) <= 0.01) // Completamente pagados
      .reduce((sum: number, lot: any) => {
        if (Array.isArray(lot.idLotes) && lot.idLotes.length > 1) {
          return sum + lot.idLotes.length;
        }
        return sum + 1;
      }, 0);

    // Total de lotes del proyecto (de configuración)
    const totalLotes = config?.lotes || 0;

    // Calcular disponibles
    const disponibles = Math.max(0, totalLotes - vendidos - creditoPendiente - creditoPagado);

    const todayRevenue = expenses
      .filter((e: any) => {
        const today = new Date().toDateString();
        return new Date(e.fecha).toDateString() === today;
      })
      .reduce((sum: number, e: any) => sum + (parseFloat(e.monto) || 0), 0);

    return {
      totalLots: totalLotes,
      availableLots: disponibles,
      soldLots: vendidos, // Solo lotes de contado
      creditLots: creditoPendiente, // Solo créditos con saldo pendiente
      paidCreditLots: creditoPagado, // Créditos completamente pagados
      todayRevenue,
      totalClients: lots.filter((lot: any) => lot.buyerName).length
    };
  }, [lots, expenses, config]);

  // Función para obtener alertas de vencimientos usando cronograma detallado
  const getPaymentAlerts = useCallback(() => {
    if (!lots) return { overdue: [], dueSoon: [], total: 0 };

    const alerts = {
      overdue: [] as any[],
      dueSoon: [] as any[],
      total: 0
    };

    lots.forEach((lot: any) => {
      // Solo procesar lotes a crédito
      if (lot.paymentType === 'contado' || (lot.paymentType !== 'pagos' && lot.status !== 'crédito')) return;

      const financialInfo = getClientFinancialInfo(lot);
      if (!financialInfo || financialInfo.saldoPendiente <= 0) return;

      // Usar generateCuotasDetalladas para obtener el cronograma real
      const cuotasDetalladas = generateCuotasDetalladas(lot);
      if (!cuotasDetalladas || cuotasDetalladas.length === 0) return;

      const today = new Date();
      const clientName = lot.buyerName || lot.comprador;
      const loteNum = Array.isArray(lot.idLotes) ? lot.idLotes.join(',') : (lot.numero || lot.id);

      // Encontrar cuotas vencidas y próximas a vencer
      const cuotasVencidas = cuotasDetalladas.filter(cuota =>
        cuota.estado === 'Vencida'
      );

      const cuotasProximasVencer = cuotasDetalladas.filter(cuota => {
        if (cuota.estado !== 'Pendiente') return false;
        const diasHastaVencimiento = Math.floor(
          (cuota.fechaVencimientoDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        return diasHastaVencimiento <= 7 && diasHastaVencimiento >= 0;
      });

      // Si hay cuotas vencidas
      if (cuotasVencidas.length > 0) {
        const totalVencido = cuotasVencidas.reduce((sum, cuota) => sum + cuota.monto, 0);
        const cuotaMasAntigua = cuotasVencidas[0];
        const diasVencida = Math.floor(
          (today.getTime() - cuotaMasAntigua.fechaVencimientoDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        alerts.overdue.push({
          clientName,
          loteNum,
          daysOverdue: diasVencida,
          cuotasVencidas: cuotasVencidas.map(c => ({
            numero: c.numero,
            fecha: c.fechaVencimiento,
            monto: c.monto,
            diasVencida: Math.floor((today.getTime() - c.fechaVencimientoDate.getTime()) / (1000 * 60 * 60 * 24))
          })),
          totalVencido,
          saldoPendiente: financialInfo.saldoPendiente
        });
      }

      // Si hay cuotas próximas a vencer
      if (cuotasProximasVencer.length > 0) {
        const proximaCuota = cuotasProximasVencer[0];
        const diasHastaVencimiento = Math.floor(
          (proximaCuota.fechaVencimientoDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );

        alerts.dueSoon.push({
          clientName,
          loteNum,
          daysUntilDue: diasHastaVencimiento,
          proximaCuota: {
            numero: proximaCuota.numero,
            fecha: proximaCuota.fechaVencimiento,
            monto: proximaCuota.monto
          },
          saldoPendiente: financialInfo.saldoPendiente
        });
      }
    });

    alerts.total = alerts.overdue.length + alerts.dueSoon.length;
    return alerts;
  }, [lots, getClientFinancialInfo, generateCuotasDetalladas]);

  // Procesador principal de consultas de negocio - SIMPLIFICADO para papá
  const processBusinessQuery = useCallback((query: string): BusinessQueryResult => {
    const queryLower = query.toLowerCase().trim();

    console.log('🔍 Consulta recibida:', query);

    // CONSULTAS ESPECÍFICAS DE SUGERENCIAS - ORDEN IMPORTANTE

    // 1. Informe financiero (debe ir ANTES de buscar números para evitar conflictos)
    if (queryLower.includes('informe financiero') || queryLower.includes('informe')) {
      // Cálculo de Flujo de Caja Proyectado (Próximo Mes) - Lógica exacta del módulo
      const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      const now = new Date();
      const mesActual = now.getMonth();
      const anioActual = now.getFullYear();
      const mesProximo = (mesActual + 1) % 12;
      const anioProximo = mesActual === 11 ? anioActual + 1 : anioActual;

      // Generar cuotas agrupadas por grupo de venta (idéntico a PaymentSystem)
      const grupos: Record<string, any[]> = {};
      lots?.filter((lot: any) => lot.status === 'crédito' && lot.paymentType === 'pagos').forEach((lot: any) => {
        const key = `${lot.seller}|${lot.buyerName}|${lot.buyerDPI}|${lot.saleDate}|${lot.receiptNumber}`;
        if (!grupos[key]) grupos[key] = [];
        grupos[key].push(lot);
      });

      let flujoCajaProyectado = 0;
      Object.values(grupos).forEach((grupo) => {
        if (grupo.length === 0) return;
        const totalValue = grupo.reduce((sum, l) => sum + (l.totalValue || 0), 0);
        const payments = grupo.flatMap(l => l.payments || []);
        const numberOfPayments = grupo[0].numberOfPayments || grupo[0].numeroPagos || 1;
        const pagoEnganche = payments.find((p: any) => p.type === 'enganche');
        const enganche = pagoEnganche ? Number(pagoEnganche.amount) : 0;
        const cuotaMensual = (totalValue - enganche) / numberOfPayments;
        const fechaVentaDate = new Date(grupo[0].saleDate || new Date());

        for (let i = 1; i <= numberOfPayments; i++) {
          const fechaVencimiento = new Date(fechaVentaDate);
          fechaVencimiento.setMonth(fechaVencimiento.getMonth() + i);
          if (fechaVencimiento.getMonth() === mesProximo && fechaVencimiento.getFullYear() === anioProximo) {
            flujoCajaProyectado += cuotaMensual;
          }
        }
      });

      // Avance General del Proyecto - Lógica exacta del módulo
      const totalLotesProyecto = businessStats?.totalLots || 0;
      const vendidos = lots?.filter((lot: any) => lot.status === 'vendido' || lot.status === 'crédito')
        .reduce((sum: number, lot: any) => {
          if (Array.isArray(lot.idLotes) && lot.idLotes.length > 1) {
            return sum + lot.idLotes.length;
          }
          return sum + 1;
        }, 0) || 0;
      const avanceProyecto = totalLotesProyecto > 0 ? (vendidos / totalLotesProyecto) * 100 : 0;

      // Cálculos financieros - Lógica exacta del módulo
      const ingresos = expenses?.filter((e: any) => ['ingreso', 'contado', 'enganche', 'abono'].includes((e.tipo || '').toLowerCase())) || [];
      const egresos = expenses?.filter((e: any) => ['egreso', 'comision'].includes((e.tipo || '').toLowerCase())) || [];
      const totalIngresos = ingresos.reduce((sum: number, e: any) => sum + (parseFloat(e.monto) || 0), 0);
      const totalEgresos = egresos.reduce((sum: number, e: any) => sum + (parseFloat(e.monto) || 0), 0);
      const utilidadBruta = totalIngresos - totalEgresos;
      const proyeccionAnual = lots?.filter((l: any) => l.status === 'vendido' || l.status === 'crédito').reduce((sum: number, l: any) => sum + (l.totalValue || 0), 0) || 0;

      // Agrupar ingresos por mes
      const ingresosPorMes: { [k: string]: number } = {};
      ingresos.forEach((e: any) => {
        const mes = e.fecha ? months[new Date(e.fecha).getMonth()] : 'Sin fecha';
        ingresosPorMes[mes] = (ingresosPorMes[mes] || 0) + (parseFloat(e.monto) || 0);
      });

      const mesesConIngresos = Object.keys(ingresosPorMes).length;
      const promedioMensual = mesesConIngresos > 0 ? totalIngresos / mesesConIngresos : 0;

      // Mejor mes
      let mejorMes = '-';
      let maxIngreso = 0;
      Object.entries(ingresosPorMes).forEach(([mes, valor]) => {
        if (valor > maxIngreso) {
          maxIngreso = valor;
          mejorMes = mes;
        }
      });

      // Eficiencia: % cobrado respecto a proyección
      const eficiencia = proyeccionAnual > 0 ? (totalIngresos / proyeccionAnual) * 100 : 0;

      return {
        answer: `📊 **INFORME FINANCIERO GENERAL**\n\n💰 **Flujo de Caja Proyectado (Próx. Mes)**\n• **Q.${flujoCajaProyectado.toLocaleString()}**\n• Ingresos estimados del próximo mes\n\n📈 **Avance General del Proyecto**\n• **${avanceProyecto.toFixed(1)}%**\n• ${vendidos} / ${totalLotesProyecto} lotes vendidos\n\n💵 **RESUMEN FINANCIERO**\n• **Total Ingresos**: Q.${totalIngresos.toLocaleString()}\n• **Total Egresos**: Q.${totalEgresos.toLocaleString()}\n• **Utilidad Bruta**: Q.${utilidadBruta.toLocaleString()}\n• **Proyección Anual**: Q.${proyeccionAnual.toLocaleString()}\n\n📊 **MÉTRICAS MENSUALES**\n• **Promedio Mensual**: Q.${promedioMensual.toLocaleString()}\n• **Mejor Mes**: ${mejorMes}\n• **Eficiencia de Cobranza**: ${eficiencia.toFixed(1)}%\n\n📋 **RESUMEN DE LOTES**\n• **Total lotes**: ${businessStats?.totalLots || 0}\n• **Lotes disponibles**: ${businessStats?.availableLots || 0}\n• **Lotes a crédito**: ${businessStats?.creditLots || 0}\n• **Lotes pagados**: ${businessStats?.paidCreditLots || 0}\n• **Lotes de contado**: ${businessStats?.soldLots || 0}`,
        confidence: 1.0,
        dataFound: true,
        suggestions: [
          'Flujo de caja detallado',
          'Vencidas por vendedor',
          'Fecha de pago Lote'
        ]
      };
    }

    // 2. Cobros del mes por vendedor
    if (queryLower.includes('cobros del mes por vendedor') || queryLower.includes('cobros mes vendedor')) {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      const currentMonthName = months[currentMonth];

      // Usar la misma lógica del reporte
      const { calcularCobrosDelMes } = require('../utils/cobrosReporte');
      const datosReporte = calcularCobrosDelMes(lots || [], expenses || [], sellers || [], currentMonth, currentYear);

      let respuesta = `💰 **COBROS DEL MES ACTUAL (${currentMonthName} de ${currentYear})**\n\n`;
      let vendorBlocks: { [key: string]: string } = {};

      if (datosReporte.length === 0) {
        respuesta += `✅ **¡Excelente!** No hay cuotas pendientes del mes.\n\n🟢 **Todos los clientes están al día con sus pagos.**`;
      } else {
        // Agrupar por vendedor
        const vendorGroups: { [key: string]: any[] } = {};

        datosReporte.forEach((registro: any) => {
          if (!vendorGroups[registro.vendedorNombre]) {
            vendorGroups[registro.vendedorNombre] = [];
          }
          vendorGroups[registro.vendedorNombre].push(registro);
        });

        Object.entries(vendorGroups).forEach(([vendedor, registros]) => {
          const lista = registros as any[];
          const totalACobrarVendedor = lista.reduce((sum, r) => sum + (r.totalSaldo || 0), 0);

          let vendorContent = `👨‍💼 **${vendedor}** (${lista.length} cliente${lista.length > 1 ? 's' : ''})\n`;
          vendorContent += `💰 **Total a cobrar: Q.${totalACobrarVendedor.toLocaleString()}**\n\n`;

          lista.forEach((registro: any) => {
            vendorContent += `• **${registro.comprador}** (Lote ${registro.lotes.join(', ')})\n`;

            // Mostrar todas las cuotas del mes (vencidas y pendientes)
            const cuotasDetalle = registro.cuotas || [];
            if (cuotasDetalle.length > 0) {
              cuotasDetalle.forEach((cuota: any) => {
                if (cuota.estado === 'Vencida') {
                  const diasVencida = Math.floor((now.getTime() - new Date(cuota.fecha).getTime()) / (1000 * 60 * 60 * 24));
                  vendorContent += `❌ Cuota #${cuota.cuota} (${cuota.fecha}) - Q.${cuota.saldo.toLocaleString()} - ${diasVencida} días vencida\n`;
                } else if (cuota.estado === 'Próxima' || cuota.estado === 'Pendiente') {
                  const diasHasta = Math.ceil((new Date(cuota.fecha).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                  vendorContent += `⏰ Cuota #${cuota.cuota} (${cuota.fecha}) - Q.${cuota.saldo.toLocaleString()} - Vence en: ${diasHasta} días\n`;
                }
              });
            }

            vendorContent += `💰 Total a cobrar: Q.${(registro.totalSaldo || 0).toLocaleString()}\n\n`;
          });

          // Guardar el contenido específico del vendedor
          vendorBlocks[vendedor] = vendorContent;

          // Agregar al contenido general
          respuesta += vendorContent;
          respuesta += `────────────────────────────────────────\n\n`;
        });
      }

      return {
        answer: respuesta,
        confidence: 1.0,
        dataFound: true,
        suggestions: [
          'Contactar vendedores',
          'Ver cliente específico',
          'Informe financiero',
          'Fecha de pago Lote'
        ],
        vendorBlocks
      };
    }

    // 3. Lotes completamente pagados
    if (queryLower.includes('lotes pagados') || queryLower.includes('lotes completamente pagados') || queryLower.includes('pagados completos')) {
      // Filtrar lotes con saldo en 0
      const lotesPagados = lots?.filter((lot: any) => {
        // Solo procesar lotes vendidos o a crédito
        if (!lot.status || lot.status === 'disponible') return false;

        // Calcular saldo pendiente
        let totalPagado = 0;
        if (lot.paymentType === 'contado') {
          totalPagado = lot.totalValue || 0;
        } else {
          const enganche = parseFloat(lot.payments?.find((p: any) => p.type === 'enganche')?.amount || 0);
          const abonos = lot.payments?.filter((p: any) => p.type === 'abono').reduce((sum: number, p: any) => sum + Number(p.amount), 0) || 0;
          totalPagado = enganche + abonos;
        }
        const saldoPendiente = (lot.totalValue || 0) - totalPagado;

        // Considerar pagado si el saldo es 0 o muy cercano a 0 (por redondeos)
        return saldoPendiente <= 0.01;
      }) || [];

      let respuesta = `✅ **LOTES COMPLETAMENTE PAGADOS**\n\n`;

      if (lotesPagados.length === 0) {
        respuesta += `📊 **No hay lotes completamente pagados aún.**\n\n`;
        respuesta += `💡 Los lotes aparecerán aquí cuando su saldo llegue a Q.0.00`;
      } else {
        // Contar clientes únicos
        const clientesUnicos = new Set(lotesPagados.map((l: any) => l.buyerName || l.comprador)).size;

        // Contar lotes individuales totales (expandiendo grupos)
        const totalLotesIndividuales = lotesPagados.reduce((sum: number, l: any) => {
          if (Array.isArray(l.idLotes) && l.idLotes.length > 1) {
            return sum + l.idLotes.length;
          }
          return sum + 1;
        }, 0);

        respuesta += `📊 **Total: ${clientesUnicos} cliente${clientesUnicos > 1 ? 's' : ''} | ${totalLotesIndividuales} lote${totalLotesIndividuales > 1 ? 's' : ''}**\n\n`;

        // Agrupar por vendedor
        const porVendedor: { [key: string]: any[] } = {};

        lotesPagados.forEach((lot: any) => {
          const vendedorId = lot.seller || lot.vendedor || lot.sellerName;
          let vendedorNombre = 'Sin vendedor';

          if (vendedorId && sellers && sellers.length > 0) {
            const vendedorData = sellers.find((s: any) =>
              s.id === vendedorId || s.name === vendedorId
            );
            vendedorNombre = vendedorData?.name || vendedorId;
          }

          if (!porVendedor[vendedorNombre]) {
            porVendedor[vendedorNombre] = [];
          }
          porVendedor[vendedorNombre].push(lot);
        });

        // Mostrar por vendedor
        Object.entries(porVendedor).forEach(([vendedor, lotes]) => {
          const totalValor = lotes.reduce((sum: number, l: any) => sum + (l.totalValue || 0), 0);

          respuesta += `👨‍💼 **${vendedor}** (${lotes.length} lote${lotes.length > 1 ? 's' : ''})\n`;
          respuesta += `💰 **Valor total vendido: Q.${totalValor.toLocaleString()}**\n\n`;

          lotes.forEach((lot: any) => {
            const loteNum = Array.isArray(lot.idLotes) ? lot.idLotes.join(', ') : (lot.numero || lot.id);
            const comprador = lot.buyerName || lot.comprador || 'Sin nombre';
            const fechaVenta = lot.saleDate ? new Date(lot.saleDate).toLocaleDateString('es-GT') : 'Sin fecha';
            const tipoVenta = lot.paymentType === 'contado' ? '💵 Contado' : '💳 Crédito';

            respuesta += `• **Lote ${loteNum}** - ${comprador}\n`;
            respuesta += `  ${tipoVenta} | ${fechaVenta} | Q.${(lot.totalValue || 0).toLocaleString()}\n\n`;
          });
        });

        // Resumen final
        const totalVendido = lotesPagados.reduce((sum: number, l: any) => sum + (l.totalValue || 0), 0);

        respuesta += `────────────────────────────────────────\n`;
        respuesta += `💰 **TOTAL COBRADO: Q.${totalVendido.toLocaleString()}**\n`;
        respuesta += `📊 **${clientesUnicos} cliente${clientesUnicos > 1 ? 's' : ''} | ${totalLotesIndividuales} lote${totalLotesIndividuales > 1 ? 's' : ''} con pago completo**`;
      }

      return {
        answer: respuesta,
        confidence: 1.0,
        dataFound: true,
        suggestions: [
          'Informe financiero',
          'Lotes a crédito',
          'Ver cliente específico',
          'Cobros del mes por vendedor'
        ]
      };
    }

    // 4. Vencidas por vendedor
    if (queryLower.includes('vencidas por vendedor') || queryLower.includes('vencidas vendedor')) {
      const alerts = getPaymentAlerts();
      const vendedoresVencidas: { [key: string]: any[] } = {};

      // Agrupar por vendedor - solo cuotas vencidas
      alerts.overdue.forEach((alert: any) => {
        const lote = lots?.find((l: any) => {
          const loteNum = Array.isArray(l.idLotes) ? l.idLotes.join(',') : (l.numero || l.id);
          return loteNum === alert.loteNum;
        });

        // Obtener el ID del vendedor del lote
        const vendedorId = lote?.seller || lote?.vendedor || lote?.sellerName;

        // Buscar el nombre del vendedor en la tabla de sellers
        let vendedorNombre = 'Sin vendedor';
        if (vendedorId && sellers && sellers.length > 0) {
          const vendedorData = sellers.find((s: any) =>
            s.id === vendedorId || s.name === vendedorId
          );
          vendedorNombre = vendedorData?.name || vendedorId;
        }

        const vendedor = vendedorNombre;

        if (!vendedoresVencidas[vendedor]) {
          vendedoresVencidas[vendedor] = [];
        }
        vendedoresVencidas[vendedor].push(alert);
      });

      let respuesta = `🚨 **CUOTAS VENCIDAS POR VENDEDOR**\n\n`;

      if (Object.keys(vendedoresVencidas).length === 0) {
        respuesta += `✅ **¡Excelente!** No hay cuotas vencidas.\n\n🟢 **Todos los clientes están al día con sus pagos.**`;
      } else {
        Object.entries(vendedoresVencidas).forEach(([vendedor, vencidas]) => {
          const totalMontoVencido = vencidas.reduce((sum, v) => sum + (v.totalVencido || 0), 0);
          respuesta += `👨‍💼 **${vendedor}** (${vencidas.length} cliente${vencidas.length > 1 ? 's' : ''} - Q.${totalMontoVencido.toLocaleString()} vencido)\n`;

          vencidas.slice(0, 5).forEach((v: any) => {
            respuesta += `   • **${v.clientName}** (Lote ${v.loteNum})\n`;

            if (v.cuotasVencidas && v.cuotasVencidas.length > 0) {
              if (v.cuotasVencidas.length === 1) {
                const cuota = v.cuotasVencidas[0];
                respuesta += `     ❌ Cuota #${cuota.numero} (${cuota.fecha}) - Q.${cuota.monto.toLocaleString()} - ${cuota.diasVencida} días vencida\n`;
              } else {
                respuesta += `     ❌ **${v.cuotasVencidas.length} cuotas vencidas:**\n`;
                v.cuotasVencidas.forEach((cuota: any) => {
                  respuesta += `       • Cuota #${cuota.numero} (${cuota.fecha}) - Q.${cuota.monto.toLocaleString()}\n`;
                });
                respuesta += `     💰 **Total a pagar para ponerse al día: Q.${v.totalVencido.toLocaleString()}**\n`;
              }
            }
            respuesta += '\n';
          });
        });
      }

      return {
        answer: respuesta,
        confidence: 1.0,
        dataFound: true,
        suggestions: [
          'Contactar vendedores',
          'Ver cliente específico',
          'Informe financiero',
          'Cobros del mes por vendedor'
        ]
      };
    }

    // 4. Fecha de pago Lote
    if (queryLower.includes('fecha de pago') && queryLower.includes('lote')) {
      // Extraer número de lote si está presente
      const loteMatch = query.match(/\b(\d{1,3})\b/);
      if (loteMatch) {
        const loteNum = loteMatch[0];
        console.log('📅 Buscando fecha de pago para lote:', loteNum);

        const lote = lots?.find((lot: any) => {
          if (Array.isArray(lot.idLotes)) {
            return lot.idLotes.includes(loteNum);
          }
          return (lot.numero?.toString() === loteNum || lot.id?.toString() === loteNum);
        });

        if (lote) {
          return generatePaymentInfo(lote, `Fecha de pago Lote ${loteNum}`);
        }
      }

      // Si no hay número específico, mostrar instrucciones
      return {
        answer: `💰 **FECHA DE PAGO LOTE**\n\n🔍 **Para ver las fechas de pago, especifica el número del lote:**\n\n**Ejemplo**: "Fecha de pago Lote 222"\n\n📋 **Lotes con crédito activo**:\n${lots?.filter((lot: any) => lot.paymentType !== 'contado' && (lot.buyerName || lot.comprador)).slice(0, 8).map((lot: any, idx: number) => {
          const name = lot.buyerName || lot.comprador;
          const loteNum = Array.isArray(lot.idLotes) ? lot.idLotes.join(',') : (lot.numero || lot.id);
          return `${idx + 1}. ${name} (Lote ${loteNum})`;
        }).join('\n') || 'No hay lotes a crédito registrados'}`,
        confidence: 0.8,
        dataFound: false,
        suggestions: [
          'Fecha de pago Lote 222',
          'Fecha de pago Lote 88',
          'Fecha de pago Lote 54',
          'Cuotas vencidas'
        ]
      };
    }

    // BÚSQUEDA SIMPLIFICADA: 
    // 6. Buscar por número de lote (ej: "222", "lote 222")
    const loteMatch = query.match(/\b(\d{1,3})\b/);
    if (loteMatch) {
      const loteNum = loteMatch[0];
      console.log('📍 Buscando por número de lote:', loteNum);

      const lote = lots?.find((lot: any) => {
        if (Array.isArray(lot.idLotes)) {
          return lot.idLotes.includes(loteNum);
        }
        return (lot.numero?.toString() === loteNum || lot.id?.toString() === loteNum);
      });

      if (lote) {
        return generateCompleteInfo(lote, `Lote ${loteNum}`);
      }
    }

    // 7. Buscar por nombre del comprador (cualquier palabra que no sea comando)
    const excludeWords = ['lote', 'cuanto', 'quien', 'como', 'donde', 'cuando', 'disponible', 'vendido'];
    const possibleName = query.split(' ').find(word =>
      word.length > 2 && !excludeWords.includes(word.toLowerCase())
    );

    if (possibleName) {
      console.log('👤 Buscando por nombre:', possibleName);
      const allMatches = findAllClientsWithNameMatch(possibleName);
      if (allMatches.length > 0) {
        return generateMultipleCompleteInfo(allMatches, possibleName);
      }
    }

    // 7. Consultas especiales simples
    if (queryLower.includes('disponible')) {
      const availableLots = getAvailableLots();
      return {
        answer: `🏠 **Lotes Disponibles**\n\n📊 Hay **${availableLots.length}** lotes disponibles para venta.\n\n💡 **Para buscar información específica, simplemente escribe:**\n• El nombre del cliente (ej: "Rufus")\n• El número del lote (ej: "222")`,
        confidence: 1.0,
        dataFound: true,
        suggestions: [
          'Ver lote 222',
          'Buscar Rufus',
          'Guerra Juan',
          'Lote 88'
        ]
      };
    }



    // 5. Consultas de vencimientos y alertas generales
    if (queryLower.includes('vencimiento') || queryLower.includes('atraso') || queryLower.includes('vencida') || queryLower.includes('pendiente')) {
      const alerts = getPaymentAlerts();

      let respuesta = `⚠️ **ALERTAS DE PAGOS**\n\n`;

      if (alerts.overdue.length > 0) {
        respuesta += `🚨 **CUOTAS VENCIDAS (${alerts.overdue.length})**:\n`;
        alerts.overdue.slice(0, 8).forEach((alert: any, idx: number) => {
          respuesta += `${idx + 1}. **${alert.clientName}** (Lote ${alert.loteNum})\n`;

          if (alert.cuotasVencidas && alert.cuotasVencidas.length > 0) {
            if (alert.cuotasVencidas.length === 1) {
              const cuota = alert.cuotasVencidas[0];
              respuesta += `   ❌ Cuota #${cuota.numero} (${cuota.fecha}) - Q.${cuota.monto.toLocaleString()}\n`;
              respuesta += `   ⏰ ${cuota.diasVencida} días de atraso\n`;
            } else {
              respuesta += `   ❌ **${alert.cuotasVencidas.length} cuotas vencidas:**\n`;
              alert.cuotasVencidas.forEach((cuota: any) => {
                respuesta += `     • Cuota #${cuota.numero} (${cuota.fecha}) - Q.${cuota.monto.toLocaleString()}\n`;
              });
              respuesta += `   💰 **Total vencido: Q.${alert.totalVencido.toLocaleString()}**\n`;
            }
          }
          respuesta += `   📊 Saldo total pendiente: Q.${alert.saldoPendiente.toLocaleString()}\n\n`;
        });
      }

      if (alerts.dueSoon.length > 0) {
        respuesta += `⏰ **PRÓXIMAS A VENCER (${alerts.dueSoon.length})**:\n`;
        alerts.dueSoon.slice(0, 5).forEach((alert: any, idx: number) => {
          respuesta += `${idx + 1}. **${alert.clientName}** (Lote ${alert.loteNum})\n`;

          if (alert.proximaCuota) {
            respuesta += `   ⚠️ Cuota #${alert.proximaCuota.numero} (${alert.proximaCuota.fecha}) - Q.${alert.proximaCuota.monto.toLocaleString()}\n`;
            respuesta += `   ⏰ Vence en: ${alert.daysUntilDue} días\n`;
          }
          respuesta += `   📊 Saldo total pendiente: Q.${alert.saldoPendiente.toLocaleString()}\n\n`;
        });
      }

      if (alerts.total === 0) {
        respuesta += `✅ **¡Excelente!** No hay cuotas vencidas ni próximas a vencer.\n\n`;
        respuesta += `🟢 **Todos los clientes están al día con sus pagos.**`;
      }

      return {
        answer: respuesta,
        confidence: 1.0,
        dataFound: true,
        suggestions: [
          'Contactar clientes',
          'Ver específico cliente',
          'Próximos 30 días',
          'Buscar cliente'
        ]
      };
    }

    // Si no encontró nada, mostrar ayuda simple
    return {
      answer: `❌ **No encontrado: "${query}"**\n\n💡 **BÚSQUEDA SIMPLE - Escribe solo:**\n\n👤 **Por nombre del cliente:**\n• "Rufus" \n• "Guerra Juan"\n• "marquez"\n\n🏠 **Por número de lote:**\n• "222"\n• "88" \n• "205"\n\n📋 **Clientes disponibles**:\n${lots?.filter((lot: any) => lot.buyerName || lot.comprador).slice(0, 8).map((lot: any, idx: number) => {
        const name = lot.buyerName || lot.comprador;
        const loteNum = Array.isArray(lot.idLotes) ? lot.idLotes.join(',') : (lot.numero || lot.id);
        return `${idx + 1}. ${name} (Lote ${loteNum})`;
      }).join('\n') || 'No hay clientes registrados'}`,
      confidence: 0.5,
      dataFound: false,
      suggestions: [
        'Rufus',
        '222',
        'Guerra Juan'
      ]
    };
  }, [lots, expenses, sellers, findClientByName, findAllClientsWithNameMatch, generateMultipleCompleteInfo, getClientFinancialInfo, getAvailableLots, getPaymentAlerts, businessStats, generateCompleteInfo, generatePaymentInfo, generateCuotasDetalladas]);

  return {
    processBusinessQuery,
    findClientByName,
    findAllClientsWithNameMatch,
    generateMultipleCompleteInfo,
    getClientPayments,
    getClientFinancialInfo,
    getPaymentAlerts,
    businessStats
  };
}
